import clsx from 'clsx';
import { cloneElement, isValidElement, type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';

export function PageHeader({
  title,
  hint,
  actions,
}: {
  title: string;
  hint?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="flex flex-col items-start gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <div className="flex flex-col gap-1">
        <h1 className="text-[19px] font-bold tracking-[-0.3px]">{title}</h1>
        {hint ? <p className="text-xs text-muted leading-relaxed">{hint}</p> : null}
      </div>
      {actions ? <div className="flex w-full shrink-0 flex-wrap gap-2 sm:w-auto">{actions}</div> : null}
    </header>
  );
}

export function Button({
  children,
  onClick,
  variant = 'default',
  disabled,
  type = 'button',
  title,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'primary' | 'dark' | 'ghost';
  disabled?: boolean;
  type?: 'button' | 'submit';
  title?: string;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={clsx(
        'inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-[13px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-40',
        variant === 'primary' && 'bg-accent text-white hover:brightness-110',
        variant === 'dark' && 'bg-ink text-white hover:brightness-125',
        variant === 'default' && 'border-[1.5px] border-hairline hover:bg-surface-muted',
        variant === 'ghost' && 'text-accent hover:underline',
      )}
    >
      {children}
    </button>
  );
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={clsx('rounded-xl border border-hairline p-4', className)}>{children}</section>;
}

export function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={clsx(
        'rounded-xl bg-surface-muted p-4 text-[11px] leading-relaxed text-[#5a5a55]',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  const labelId = useId();
  const labelledChild = isValidElement<{ 'aria-labelledby'?: string }>(children)
    ? cloneElement(children, {
        'aria-labelledby': children.props['aria-labelledby'] ?? labelId,
      })
    : children;

  return (
    <fieldset className="flex min-w-0 flex-col gap-1 border-0 p-0">
      <legend id={labelId} className="text-[11px] text-muted">
        {label}
      </legend>
      {labelledChild}
      {hint ? <span className="text-[11px] text-faint">{hint}</span> : null}
    </fieldset>
  );
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={clsx(
        'rounded-lg border border-hairline px-3 py-2 text-[13px] outline-none focus:border-ink',
        props.className,
      )}
    />
  );
}

export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="-mx-1 w-[calc(100%+0.5rem)] min-w-0 max-w-[calc(100%+0.5rem)] overflow-x-auto rounded-xl border border-hairline sm:mx-0 sm:w-full sm:max-w-full">
      <table className="w-full min-w-[520px] border-collapse text-[13px]">{children}</table>
    </div>
  );
}

export function Th({
  children,
  align = 'left',
  sticky = false,
}: {
  children: ReactNode;
  align?: 'left' | 'right';
  /** Pins the column to the right edge so an action stays reachable on a phone. */
  sticky?: boolean;
}) {
  return (
    <th
      className={clsx(
        'bg-surface-muted px-3 py-2 text-[11px] font-normal text-muted',
        align === 'right' ? 'text-right' : 'text-left',
        sticky && 'sticky right-0 z-10 shadow-[-8px_0_8px_-8px_rgba(11,11,11,0.15)]',
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  align = 'left',
  mono = false,
  strong = false,
  sticky = false,
}: {
  children: ReactNode;
  align?: 'left' | 'right';
  mono?: boolean;
  strong?: boolean;
  /** Pairs with the matching Th; needs an opaque background to sit over cells. */
  sticky?: boolean;
}) {
  return (
    <td
      className={clsx(
        'border-t border-hairline px-3 py-2.5',
        align === 'right' ? 'text-right' : 'text-left',
        mono && 'num',
        strong && 'font-semibold',
        sticky && 'sticky right-0 z-10 bg-white shadow-[-8px_0_8px_-8px_rgba(11,11,11,0.15)]',
      )}
    >
      {children}
    </td>
  );
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'danger';
}) {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium',
        tone === 'neutral' && 'bg-surface-muted text-ink',
        tone === 'success' && 'bg-success/12 text-success',
        tone === 'warning' && 'bg-warning/12 text-warning',
        tone === 'danger' && 'bg-danger/12 text-danger',
      )}
    >
      {children}
    </span>
  );
}

export function Issues({
  issues,
}: {
  issues: Array<{ level: 'error' | 'warning'; code: string; message: string }>;
}) {
  const { t, i18n } = useTranslation();
  if (issues.length === 0) {
    return <p className="text-xs text-success">{t('common.noIssues')}</p>;
  }
  return (
    <ul className="flex flex-col gap-1.5 text-xs">
      {issues.map((issue) => (
        <li
          key={`${issue.code}:${issue.message}`}
          className={issue.level === 'error' ? 'text-danger' : 'text-warning'}
        >
          {/* The server's `code` is the contract; its `message` is an untranslated
              developer-facing fallback for codes the dictionary doesn't know. */}
          {issue.level === 'error' ? '✕' : '!'}{' '}
          {i18n.exists(`issues.${issue.code}`) ? t(`issues.${issue.code}`) : issue.message}
        </li>
      ))}
    </ul>
  );
}

export function Loading() {
  const { t } = useTranslation();
  return <p className="py-10 text-center text-sm text-muted">{t('common.loading')}</p>;
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <p className="rounded-lg border border-danger/40 bg-danger/5 px-3 py-2 text-xs text-danger">{message}</p>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-8 text-center text-sm text-muted">{children}</p>;
}
